import React, { useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from '@mui/material';
import Typography from '@mui/material/Typography';
import { LoadingButton } from '@mui/lab';
import Button from '@mui/material/Button';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { AccountData, CatalogCategoryOut, CatalogService, OrderPageData, PartialLabelData } from '../api';
import { useAuth } from '../session/InternalAuthProvider';
import { useLanguage } from '../common/GeneralUtils';
import { getOrderPages } from '../utils/AccountUtils';
import { OrderPagesMultiSelect } from '../userSettings/OrderPagesMultiSelect';
import ConfirmationDialog from '../common/ui/ConfimationDialog';
import { useApiResponseHandlerContext } from '../common/apiNotifications/useApiResponseHandlerContext';
import { ApiResponseHandlerType } from '../common/apiNotifications/ApiResponseHandlerContext';
import { labelHasValue, trimLabel } from './catalogUtils';

type EditCategoryDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  category?: CatalogCategoryOut;
  nextSortOrder: number;
  onSaved: (category: CatalogCategoryOut) => void;
};

type CategoryFormValues = {
  label: PartialLabelData;
  order_page_ids: string[];
};

export const EditCategoryDialog = ({ isOpen, onClose, category, nextSortOrder, onSaved }: EditCategoryDialogProps) => {
  const { t } = useTranslation();
  // @ts-ignore
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const language = useLanguage();
  const { showResponseNotification } = useApiResponseHandlerContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [archiveConfirmIsOpen, setArchiveConfirmIsOpen] = useState(false);
  const languages = account.customer_facing_widgets_languages?.length
    ? account.customer_facing_widgets_languages
    : [language];

  const allOrderPageIds = getOrderPages(authState.account).map((page: OrderPageData) => page.id);

  const validationSchema = yup.object().shape({
    label: yup
      .object()
      .test('has-label', t('catalog.atLeastOneLanguage'), (value) => labelHasValue(value as PartialLabelData)),
    order_page_ids: yup.array().min(allOrderPageIds.length ? 1 : 0, t('catalog.selectAtLeastOnePage')),
  });

  // An empty stored list means "shown everywhere", so the form surfaces it as every page ticked.
  const initialValues: CategoryFormValues = {
    label: { en: category?.label?.en ?? undefined, he: category?.label?.he ?? undefined },
    order_page_ids: category?.order_page_ids?.length ? category.order_page_ids : allOrderPageIds,
  };

  const showApiError = (error: any) => {
    const detail = error?.body?.detail;
    const message =
      detail === 'Category has active items' ? t('catalog.hasActiveItems') : detail || t('api.error.default');
    showResponseNotification(message, ApiResponseHandlerType.ERROR);
  };

  const onSubmit = async (values: CategoryFormValues) => {
    try {
      setIsSubmitting(true);
      // Every page ticked is stored as an empty list, so pages created later join automatically.
      const coversAllPages = allOrderPageIds.every((id) => values.order_page_ids.includes(id));
      const payload = { label: trimLabel(values.label), order_page_ids: coversAllPages ? [] : values.order_page_ids };
      const savedCategory = category
        ? await CatalogService.updateCatalogCategory({ ...payload, id: category.id, sort_order: category.sort_order })
        : await CatalogService.createCatalogCategory({ ...payload, sort_order: nextSortOrder });
      onSaved(savedCategory);
      onClose();
    } catch (error: any) {
      showApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onArchiveConfirmClose = async (actionConfirmed: boolean) => {
    if (actionConfirmed && category) {
      try {
        const archivedCategory = await CatalogService.archiveCatalogCategory({ id: category.id });
        onSaved(archivedCategory);
        setArchiveConfirmIsOpen(false);
        onClose();
        return;
      } catch (error: any) {
        showApiError(error);
      }
    }
    setArchiveConfirmIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{category ? t('catalog.editCategory') : t('catalog.addCategory')}</DialogTitle>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
        {({ values, errors, touched, handleChange, submitForm }) => (
          <Form>
            <DialogContent>
              <Grid container flexDirection="column" gap={2}>
                <Grid item container gap={2} flexWrap="nowrap">
                  {languages.map((formLanguage) => (
                    <TextField
                      key={formLanguage}
                      fullWidth
                      size="small"
                      name={`label.${formLanguage}`}
                      label={`${t('catalog.categoryName')} (${t(`emailTemplates.${formLanguage}`)})`}
                      value={values.label[formLanguage] || ''}
                      onChange={handleChange}
                    />
                  ))}
                </Grid>
                {typeof errors.label === 'string' && touched.label && (
                  <Typography color="error" variant="caption">
                    {errors.label}
                  </Typography>
                )}
                <Grid item container flexDirection="column">
                  <Typography fontWeight={700}>{t('catalog.categoryScope')}</Typography>
                  <OrderPagesMultiSelect
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    orderPagesFieldName="order_page_ids"
                    account={account}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              {category && !category.archived && (
                <Button
                  color="error"
                  sx={{ textTransform: 'none', marginInlineEnd: 'auto' }}
                  onClick={() => setArchiveConfirmIsOpen(true)}>
                  {t('catalog.archive')}
                </Button>
              )}
              <Button sx={{ textTransform: 'none' }} onClick={onClose}>
                {t('cancel')}
              </Button>
              <LoadingButton
                loading={isSubmitting}
                variant="contained"
                sx={{ textTransform: 'none' }}
                onClick={submitForm}>
                {category ? t('save_changes') : t('add')}
              </LoadingButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
      {archiveConfirmIsOpen && (
        <ConfirmationDialog
          isOpen={archiveConfirmIsOpen}
          onClose={onArchiveConfirmClose}
          title={t('catalog.archiveCategoryTitle')}
          content={t('catalog.archiveCategoryContent')}
          ctaText={t('catalog.archive')}
        />
      )}
    </Dialog>
  );
};
